import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import walletService from '../../services/walletService';

interface WalletBalance {
    walletId: string;
    userId: string;
    balance: number;
    currency: string;
    lastUpdated: Date;
}

interface Transaction {
    transactionId: string;
    type: 'credit' | 'debit';
    amount: number;
    description: string;
    reference: string;
    previousBalance: number;
    newBalance: number;
    createdAt: Date;
}

interface FundingTransaction {
    transactionId: string;
    amount: number;
    description: string;
    previousBalance: number;
    newBalance: number;
    status: string;
    createdAt: Date;
}

interface WalletSummary {
    balance: number;
    currency: string;
    totalFunded: number;
    totalUsed: number;
    transactionCount: number;
    recentTransactions: Array<{
        type: 'credit' | 'debit';
        amount: number;
        description: string;
        createdAt: Date;
    }>;
}

interface WalletState {
    balance: WalletBalance | null;
    summary: WalletSummary | null;
    transactions: Transaction[];
    fundingTransactions: FundingTransaction[];
    transactionsPagination: {
        page: number;
        limit: number;
        total: number;
        pages: number;
    };
    fundingPagination: {
        page: number;
        limit: number;
        total: number;
        pages: number;
    };
    loading: boolean;
    error: string | null;
    success: string | null;
    lastUpdated: Date | null;
    balanceValidation: {
        isValid: boolean | null;
        requiredAmount: number;
        currentBalance: number;
        message: string;
    } | null;
}

const initialState: WalletState = {
    balance: null,
    summary: null,
    transactions: [],
    fundingTransactions: [],
    transactionsPagination: {
        page: 1,
        limit: 10,
        total: 0,
        pages: 0
    },
    fundingPagination: {
        page: 1,
        limit: 10,
        total: 0,
        pages: 0
    },
    loading: false,
    error: null,
    success: null,
    lastUpdated: null,
    balanceValidation: null
};

// Async thunks
export const initializeWallet = createAsyncThunk(
    'wallet/initialize',
    async (_, { rejectWithValue }) => {
        try {
            const response = await walletService.initializeWallet();
            return response;
        } catch (error: any) {
            return rejectWithValue(error.message);
        }
    }
);

export const fetchWalletBalance = createAsyncThunk(
    'wallet/fetchBalance',
    async (_, { rejectWithValue }) => {
        try {
            const response = await walletService.getBalance();
            return response;
        } catch (error: any) {
            return rejectWithValue(error.message);
        }
    }
);

export const fetchWalletSummary = createAsyncThunk(
    'wallet/fetchSummary',
    async (_, { rejectWithValue }) => {
        try {
            const response = await walletService.getSummary();
            return response;
        } catch (error: any) {
            return rejectWithValue(error.message);
        }
    }
);

export const fetchTransactionHistory = createAsyncThunk(
    'wallet/fetchTransactions',
    async (
        { page = 1, limit = 10 }: { page?: number; limit?: number } = {},
        { rejectWithValue }
    ) => {
        try {
            const response = await walletService.getTransactionHistory(page, limit);
            return response;
        } catch (error: any) {
            return rejectWithValue(error.message);
        }
    }
);

export const fetchFundingHistory = createAsyncThunk(
    'wallet/fetchFundingHistory',
    async (
        { page = 1, limit = 10 }: { page?: number; limit?: number } = {},
        { rejectWithValue }
    ) => {
        try {
            const response = await walletService.getFundingHistory(page, limit);
            return response;
        } catch (error: any) {
            return rejectWithValue(error.message);
        }
    }
);

export const validateWalletBalance = createAsyncThunk(
    'wallet/validateBalance',
    async (amount: number, { rejectWithValue }) => {
        try {
            const response = await walletService.validateBalance(amount);
            return response;
        } catch (error: any) {
            return rejectWithValue(error.message);
        }
    }
);

export const processFidelityPaymentToWallet = createAsyncThunk(
    'wallet/processFidelityPayment',
    async (fidelityPaymentId: string, { rejectWithValue }) => {
        try {
            const response = await walletService.processFidelityPayment(fidelityPaymentId);
            return response;
        } catch (error: any) {
            return rejectWithValue(error.message);
        }
    }
);

const walletSlice = createSlice({
    name: 'wallet',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        clearSuccess: (state) => {
            state.success = null;
        },
        resetWallet: (state) => {
            state.balance = null;
            state.summary = null;
            state.transactions = [];
            state.fundingTransactions = [];
            state.error = null;
            state.success = null;
            state.lastUpdated = null;
        },
        setTransactionPage: (state, action: PayloadAction<number>) => {
            state.transactionsPagination.page = action.payload;
        },
        setFundingPage: (state, action: PayloadAction<number>) => {
            state.fundingPagination.page = action.payload;
        }
    },
    extraReducers: (builder) => {
        // Initialize Wallet
        builder.addCase(initializeWallet.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(initializeWallet.fulfilled, (state, action) => {
            state.loading = false;
            state.balance = action.payload;
            state.success = 'Wallet initialized successfully';
            state.lastUpdated = new Date();
        });
        builder.addCase(initializeWallet.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string;
        });

        // Fetch Balance
        builder.addCase(fetchWalletBalance.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(fetchWalletBalance.fulfilled, (state, action) => {
            state.loading = false;
            state.balance = action.payload;
            state.lastUpdated = new Date();
        });
        builder.addCase(fetchWalletBalance.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string;
        });

        // Fetch Summary
        builder.addCase(fetchWalletSummary.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(fetchWalletSummary.fulfilled, (state, action) => {
            state.loading = false;
            state.summary = action.payload;
            state.lastUpdated = new Date();
        });
        builder.addCase(fetchWalletSummary.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string;
        });

        // Fetch Transactions
        builder.addCase(fetchTransactionHistory.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(fetchTransactionHistory.fulfilled, (state, action) => {
            state.loading = false;
            state.transactions = action.payload.data;
            state.transactionsPagination = action.payload.pagination;
            state.lastUpdated = new Date();
        });
        builder.addCase(fetchTransactionHistory.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string;
        });

        // Fetch Funding History
        builder.addCase(fetchFundingHistory.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(fetchFundingHistory.fulfilled, (state, action) => {
            state.loading = false;
            state.fundingTransactions = action.payload.data;
            state.fundingPagination = action.payload.pagination;
            state.lastUpdated = new Date();
        });
        builder.addCase(fetchFundingHistory.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string;
        });

        // Validate Balance
        builder.addCase(validateWalletBalance.pending, (state) => {
            state.error = null;
        });
        builder.addCase(validateWalletBalance.fulfilled, (state, action) => {
            state.error = null;
            state.balanceValidation = action.payload;
        });
        builder.addCase(validateWalletBalance.rejected, (state, action) => {
            state.error = action.payload as string;
            state.balanceValidation = null;
        });

        // Process Fidelity Payment
        builder.addCase(processFidelityPaymentToWallet.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(processFidelityPaymentToWallet.fulfilled, (state, action) => {
            state.loading = false;
            state.balance = action.payload;
            state.success = 'Wallet funded successfully';
            state.lastUpdated = new Date();
        });
        builder.addCase(processFidelityPaymentToWallet.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string;
        });
    }
});

export const {
    clearError,
    clearSuccess,
    resetWallet,
    setTransactionPage,
    setFundingPage
} = walletSlice.actions;

export default walletSlice.reducer;
